import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';

import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { buscarTitulosParaEstorno } from '~/Services/Service';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { LIMITE_LOTE_ESTORNO } from '~/constant';

import FiltrosEstorno from './components/FiltrosEstorno';
import GridTitulos from './components/GridTitulos';
import BarraAcoesEstorno from './components/BarraAcoesEstorno';
import ModalConfirmarEstorno from './components/ModalConfirmarEstorno';

const TAMANHO_PAGINA = 20;
const ORDENACAO_DEFAULT = { campo: 'data_vencimento', direcao: 'ASC' };

const construirPayloadBusca = (filtros, pagina, ordenacao) => ({
  codigo_grupo_empresa: filtros.codigoGrupoEmpresa,
  codigo_conta: filtros.codigoConta || null,
  codigo_cedente: filtros.codigoCedente || null,
  codigo_ativo: filtros.codigoAtivo || null,
  data_inicio: filtros.dataInicio || null,
  data_fim: filtros.dataFim || null,
  numero_cheque: filtros.numeroCheque || null,
  valor:
    filtros.valor != null && filtros.valor !== ''
      ? Number(filtros.valor)
      : null,
  pagina,
  tamanho_pagina: TAMANHO_PAGINA,
  ordenacao_campo: ordenacao.campo,
  ordenacao_direcao: ordenacao.direcao,
});

const EstornoBaixaTitulo = () => {
  const empreendimentos = useSelector(state => state.empreendimentos);

  const [filtros, setFiltros] = useState(null);
  const [titulos, setTitulos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [ordenacao, setOrdenacao] = useState(ORDENACAO_DEFAULT);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [
    titulosSelecionadosPorCodigo,
    setTitulosSelecionadosPorCodigo,
  ] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const totalPaginas = useMemo(
    () => (totalRegistros > 0 ? Math.ceil(totalRegistros / TAMANHO_PAGINA) : 0),
    [totalRegistros]
  );

  useEffect(() => {
    if (!filtros) return undefined;
    let ativo = true;
    const carregar = async () => {
      try {
        setCarregando(true);
        const resposta = await buscarTitulosParaEstorno(
          construirPayloadBusca(filtros, pagina, ordenacao)
        );
        if (!ativo) return;
        const itens = (resposta && resposta.itens) || [];
        const total = (resposta && resposta.total_registros) || 0;
        setTitulos(itens);
        setTotalRegistros(total);
        // Edge case (LICOES §1.4): página atual ficou vazia após filtro/estorno
        if (itens.length === 0 && pagina > 1 && total > 0) {
          const ultima = Math.max(1, Math.ceil(total / TAMANHO_PAGINA));
          setPagina(ultima);
        }
      } catch (err) {
        if (!ativo) return;
        setTitulos([]);
        setTotalRegistros(0);
        toast.error(extrairMensagemErro(err));
      } finally {
        if (ativo) setCarregando(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, [filtros, pagina, ordenacao]);

  const aplicarFiltros = useCallback(novosFiltros => {
    setSelecionados(new Set());
    setTitulosSelecionadosPorCodigo({});
    setPagina(1);
    setOrdenacao(ORDENACAO_DEFAULT);
    setFiltros(novosFiltros);
  }, []);

  const alternarSelecaoTitulo = useCallback(
    codigoTitulo => {
      const titulo = titulos.find(t => t.codigo_titulo === codigoTitulo);
      setSelecionados(anterior => {
        const proximo = new Set(anterior);
        if (proximo.has(codigoTitulo)) {
          proximo.delete(codigoTitulo);
          setTitulosSelecionadosPorCodigo(mapaAnterior => {
            const proximoMapa = { ...mapaAnterior };
            delete proximoMapa[codigoTitulo];
            return proximoMapa;
          });
        } else {
          proximo.add(codigoTitulo);
          if (titulo) {
            setTitulosSelecionadosPorCodigo(mapaAnterior => ({
              ...mapaAnterior,
              [codigoTitulo]: titulo,
            }));
          }
        }
        return proximo;
      });
    },
    [titulos]
  );

  const alternarSelecionarTodosDaPagina = useCallback(
    marcar => {
      setSelecionados(anterior => {
        const proximo = new Set(anterior);
        const mapa = { ...titulosSelecionadosPorCodigo };
        titulos.forEach(t => {
          if (marcar) {
            if (
              proximo.size >= LIMITE_LOTE_ESTORNO &&
              !proximo.has(t.codigo_titulo)
            ) {
              return;
            }
            proximo.add(t.codigo_titulo);
            mapa[t.codigo_titulo] = t;
          } else {
            proximo.delete(t.codigo_titulo);
            delete mapa[t.codigo_titulo];
          }
        });
        setTitulosSelecionadosPorCodigo(mapa);
        return proximo;
      });
    },
    [titulos, titulosSelecionadosPorCodigo]
  );

  const limparSelecao = useCallback(() => {
    setSelecionados(new Set());
    setTitulosSelecionadosPorCodigo({});
  }, []);

  const ordenarPor = useCallback(campo => {
    setOrdenacao(anterior => {
      if (anterior.campo === campo) {
        return { campo, direcao: anterior.direcao === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { campo, direcao: 'ASC' };
    });
  }, []);

  const titulosSelecionados = useMemo(
    () =>
      Array.from(selecionados)
        .map(codigo => titulosSelecionadosPorCodigo[codigo])
        .filter(titulo => !!titulo),
    [selecionados, titulosSelecionadosPorCodigo]
  );

  const abrirModal = useCallback(() => {
    if (selecionados.size === 0) {
      toast.warn('Selecione ao menos um título para estornar.');
      return;
    }
    if (selecionados.size > LIMITE_LOTE_ESTORNO) {
      toast.error(
        `Selecione no máximo ${LIMITE_LOTE_ESTORNO} títulos por estorno.`
      );
      return;
    }
    setModalAberto(true);
  }, [selecionados]);

  const fecharModal = useCallback(() => {
    if (enviando) return;
    setModalAberto(false);
  }, [enviando]);

  const recarregarAposEstorno = useCallback(async () => {
    if (!filtros) return;
    try {
      setCarregando(true);
      const resposta = await buscarTitulosParaEstorno(
        construirPayloadBusca(filtros, pagina, ordenacao)
      );
      const itens = (resposta && resposta.itens) || [];
      const total = (resposta && resposta.total_registros) || 0;
      setTitulos(itens);
      setTotalRegistros(total);
      // Edge case (LICOES §1.4): estornar último item da última página
      if (itens.length === 0 && pagina > 1) {
        const ultima = Math.max(1, Math.ceil(total / TAMANHO_PAGINA));
        setPagina(ultima);
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregando(false);
    }
  }, [filtros, pagina, ordenacao]);

  const setEnviandoSubmit = useCallback(valor => {
    setEnviando(valor);
  }, []);

  return (
    <Container fluid className="py-3">
      <Header title="Estorno de baixa de títulos" />

      <FiltrosEstorno
        empreendimentos={empreendimentos}
        onAplicar={aplicarFiltros}
        carregando={carregando}
      />

      {carregando && <Loading />}

      {filtros && !carregando && (
        <GridTitulos
          titulos={titulos}
          selecionados={selecionados}
          ordenacao={ordenacao}
          pagina={pagina}
          totalPaginas={totalPaginas}
          totalRegistros={totalRegistros}
          tamanhoPagina={TAMANHO_PAGINA}
          onAlternarTitulo={alternarSelecaoTitulo}
          onAlternarTodosDaPagina={alternarSelecionarTodosDaPagina}
          onOrdenarPor={ordenarPor}
          onMudarPagina={setPagina}
        />
      )}

      {filtros && (
        <BarraAcoesEstorno
          quantidadeSelecionada={selecionados.size}
          limite={LIMITE_LOTE_ESTORNO}
          onEstornar={abrirModal}
          onLimpar={limparSelecao}
        />
      )}

      <ModalConfirmarEstorno
        aberto={modalAberto}
        titulosSelecionados={titulosSelecionados}
        onFechar={fecharModal}
        onSucesso={async () => {
          setModalAberto(false);
          limparSelecao();
          await recarregarAposEstorno();
        }}
        enviando={enviando}
        setEnviando={setEnviandoSubmit}
      />
    </Container>
  );
};

export default EstornoBaixaTitulo;
